<template>
  <div class="content">
    <!-- Sekcja filtrów oraz przycisk szukaj -->
    <div class="d-flex filters">
      <!-- Komponent z filtrami aktywności użytkownika -->
      <FilterActivityList />
      <!-- Przycisk uruchamiający odświeżenie danych (szukanie) -->
      <button
        class="btn button-download btn-next ms-auto"
        @click="checkAndRefresh()"
      >
        {{ t('search').toUpperCase() }}
      </button>
    </div>
    <!-- Tabela z historią aktywności użytkowników -->
    <table class="user-table table table-striped">
      <thead class="thead-light">
        <tr>
          <!-- Nagłówki kolumn -->
          <th style="width: 5%">{{ t('ordinal-number') }}</th>
          <th style="width: 10%">{{ t('username') }}</th>
          <th style="width: 15%">{{ t('localization-name') }}</th>
          <th style="width: 15%">{{ t('logged-since') }}</th>
          <th style="width: 15%">{{ t('logged-to') }}</th>
          <th style="width: 10%">{{ t('duration-time') }}</th>
          <th style="width: 10%">{{ t('ip-address') }}</th>
          <th style="width: 20%">{{ t('type-event') }}</th>
        </tr>
      </thead>
      <tbody v-if="users">
        <!-- Pętla po użytkownikach/zdarzeniach -->
        <template v-for="(item, idx) in users" :key="item.id + '-' + idx">
          <tr
            id="UsersList"
            class="activity-row"
            :class="{ 'activity-row-open': isExpanded(item, idx) }"
            @click="toggleDetails(item, idx)"
          >
            <td>{{ (pageNumber - 1) * pageSize + idx + 1 }}</td>
            <td>{{ item.user?.username || "-" }}</td>
            <!-- Jeśli to sesja VMS, wyświetl id znaku, w przeciwnym razie lokalizację -->
            <td v-if="item.vms">
              VMS id: {{ item.vms.id }}
            </td>
            <td v-else>
              {{ getLocationLabel(item) }}
            </td>
            <td>{{ formatDate(getStartAt(item)) }}</td>
            <td>{{ formatDate(getEndAt(item)) }}</td>
            <!-- Czas trwania sesji -->
            <td>{{ getDurationForEvent(item) }}</td>
            <td>{{ item.ip || "-" }}</td>
            <!-- Typ zdarzenia: obsługa różnych przypadków -->
            <td>{{ getEventLabel(item) }}</td>
          </tr>
          <tr v-if="isExpanded(item, idx)" class="activity-details-row">
            <td colspan="8" class="activity-details-cell">
              <div class="activity-details-grid">
                <div
                  v-for="(detail, detailIdx) in getEventDetails(item)"
                  :key="detail.label + '-' + detailIdx"
                  class="activity-detail-item"
                >
                  <span class="activity-detail-label">{{ detail.label }}:</span>
                  <span class="activity-detail-value">{{ detail.value }}</span>
                </div>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
    <!-- Paginacja -->
    <PagePanel
      :page-num="pageNumber"
      :page-size="pageSize"
      :total-size="totalCount"
      v-on:setPage="setPage"
    />
  </div>
</template>

<script>
import moment from "moment";
import Users from "../services/Users";
import PagePanel from "@/components/table/PagePanel";
import FilterActivityList from "./FilterActivityList";
import { useUserActivityFiltersStore } from "@/stores/userActivityFilters";
import { USER_ACTIVITY_EVENT_KEY_BY_ID, USER_ACTIVITY_EVENT_TYPES } from "@/constants/userActivityEventTypes";

moment.locale("pl"); // Ustaw polski język dla dat

export default {
  components: { PagePanel, FilterActivityList },
  name: "UserActivityList",
  data() {
    return {
      loading: false,    // Czy trwa ładowanie danych
      users: [],         // Wyniki (sesje użytkowników)
      pageNumber: 1,     // Numer aktualnej strony
      pageSize: 10,      // Liczba rekordów na stronę
      totalCount: 10,    // Łączna liczba wyników
      error: null,       // Błąd (jeśli wystąpił)
      moment: moment,    // Instancja moment.js do formatowania dat
      dataService: Users, // Serwis pobierający dane
      filtersStore: useUserActivityFiltersStore(),
      expandedRowKey: null,
    };
  },
  created() {
    // Automatycznie pobierz dane po zamontowaniu komponentu
    this.retrieveUsers();
  },
  methods: {
    /**
     * Pobiera, przetwarza i buduje listę aktywności użytkowników.
     * Obsługuje zarówno zwykłe logowania jak i logowania/wylogowania na znaki VMS.
     */
    async refreshData() {
      this.loading = true;
      const pageSize = this.pageSize;
      let page = 1;
      const sessions = [];
      const seenEnableIds = new Set(); // Unikalność połączonych sesji VMS

      // Pętla przez wszystkie strony API (dopóki są wyniki)
      for (;;) {
        const { totalCount, content } = await this.dataService.getUserActivityList(page, pageSize);
        if (!content.length) break; // Brak nowych wyników, kończymy

        for (const evt of content) {
          if (!evt.vms) {
            // Zwykła sesja - logowanie do systemu/stacji
            sessions.push({
              ...evt,
              __eventKey: this.getEventKey(evt),
            });
            continue;
          }
          // Pary enable/disable na znaku VMS (aby liczyć "sesję")
          if (evt.eventType === 'enable') {
            const key = `${evt.vms.id}|${evt.createdAt}`;
            if (seenEnableIds.has(key)) continue; // Już przetworzone

            // Szukamy odpowiadającego wyłączenia
            let partner = content
              .filter(d =>
                d.vms?.id === evt.vms.id &&
                d.eventType === 'disable' &&
                new Date(d.createdAt) > new Date(evt.createdAt))
              .sort((a,b) => new Date(a.createdAt) - new Date(b.createdAt))[0];

            // Jeśli nie znaleziono na tej stronie, szukaj na kolejnych
            let lookPage = page + 1;
            while (!partner) {
              const next = await this.dataService.getUserActivityList(lookPage++, pageSize);
              if (!next.content.length) break;
              partner = next.content
                .filter(d =>
                  d.vms?.id === evt.vms.id &&
                  d.eventType === 'disable' &&
                  new Date(d.createdAt) > new Date(evt.createdAt))
                .sort((a,b) => new Date(a.createdAt) - new Date(b.createdAt))[0];
            }

            if (partner) {
              // Dodajemy sesję VMS (połącz enable/disable)
              sessions.push({
                id:       sessions.length+1,
                user:     partner.user,
                vms:      partner.vms,
                loginAt:  evt.createdAt,
                logoutAt: partner.createdAt,
                ip:       partner.ip,
                eventType: 'vms-session',
                __eventKey: "vms-session",
              });
              seenEnableIds.add(key); // Zapamiętaj że ta sesja już jest
            }
          }
        }

        // Jeśli to ostatnia strona danych, przerywamy pętlę
        if (page * pageSize >= totalCount) break;
        page++;
      }
      const selectedEventKey = this.getSelectedEventKey();
      const filteredSessions = selectedEventKey
        ? sessions.filter((evt) => evt.__eventKey === selectedEventKey)
        : sessions;

      // Obliczamy dane paginacji na frontendzie
      this.totalCount = filteredSessions.length;
      const start = (this.pageNumber - 1) * pageSize;
      this.users = filteredSessions.slice(start, start + pageSize);
      this.expandedRowKey = null;

      this.loading = false;
    },
    // Pobierz dane użytkowników po zamontowaniu komponentu
    retrieveUsers() {
      this.loading = true;
      this.refreshData();
    },
    // Ustaw stronę (przy zmianie strony paginacji)
    setPage(event) {
      this.pageNumber = event;
      this.refreshData();
    },
    getRowKey(item, idx) {
      return `${item.id}-${idx}`;
    },
    toggleDetails(item, idx) {
      const key = this.getRowKey(item, idx);
      this.expandedRowKey = this.expandedRowKey === key ? null : key;
    },
    isExpanded(item, idx) {
      return this.expandedRowKey === this.getRowKey(item, idx);
    },
    parseMetadata(metadata) {
      if (metadata == null || metadata === "") {
        return null;
      }
      if (typeof metadata === "object") {
        return metadata;
      }
      if (typeof metadata !== "string") {
        return { value: String(metadata) };
      }
      try {
        return JSON.parse(metadata);
      } catch (e) {
        return { value: metadata };
      }
    },
    formatDetailValue(value) {
      if (value == null || value === "") {
        return "-";
      }
      if (Array.isArray(value)) {
        return value.join(", ");
      }
      if (typeof value === "object") {
        return JSON.stringify(value);
      }
      return String(value);
    },
    mapFilterLabel(key) {
      const labels = {
        performedByUsername: "Użytkownik wykonujący",
        plateLpr: "Numer rejestracyjny",
        vehicleMake: "Marka pojazdu",
        vehicleModel: "Model pojazdu",
        similarity: "Podobieństwo OCR",
        minSpeed: "Min. prędkość",
        maxSpeed: "Max. prędkość",
        minTotalLoad: "Min. masa całkowita",
        maxTotalLoad: "Max. masa całkowita",
        minVehLength: "Min. długość pojazdu",
        maxVehLength: "Max. długość pojazdu",
        minVehHeight: "Min. wysokość pojazdu",
        maxVehHeight: "Max. wysokość pojazdu",
        minNumberOfAxles: "Min. liczba osi",
        maxNumberOfAxles: "Max. liczba osi",
        minOverloadSA: "Min. przeciążenie osi pojedynczej",
        maxOverloadSA: "Max. przeciążenie osi pojedynczej",
        minOverloadMA: "Min. przeciążenie osi wielokrotnej",
        maxOverloadMA: "Max. przeciążenie osi wielokrotnej",
        minOverloadTL: "Min. przeciążenie tandemu/tridemu",
        maxOverloadTL: "Max. przeciążenie tandemu/tridemu",
        overspeed: "Przekroczenie prędkości",
        wrongDirection: "Niewłaściwy kierunek",
        onBlackList: "Na czarnej liście",
        overload: "Przeciążenie",
        overheight: "Przekroczenie wysokości",
        overlane: "Wyjazd poza pas",
        recidivismOnly: "Tylko recydywa",
        withTags: "Uwzględnij tagi",
        reportType: "Typ raportu",
        dateTimeStart: "Data początkowa",
        dateTimeEnd: "Data końcowa",
        orderDirection: "Kierunek sortowania",
        orderDirection1: "Kierunek sortowania",
        orderByColumnName1: "sortowanie po kolumnie",
        withImages: "Zdjęcia",
        withAxles: "z osiami",
      };
      return labels[key] || key;
    },
    mapSourceValue(value) {
      const map = {
        "arch-vehicles/list": "Archiwum - wyszukiwanie",
        "arch-vehicles/report": "Archiwum - pobranie raportu",
        "login-to-locations/report": "Historia aktywności - pobranie raportu",
      };
      return map[value] || value;
    },
    mapReportTypeValue(value) {
      const map = {
        csv: "CSV",
        "csv-with-image": "CSV + zdjęcia",
        xlsx: "XLSX",
        "xlsx-with-image": "XLSX + zdjęcia",
        pdf: "PDF",
        "pdf-with-image": "PDF + zdjęcia",
        xml: "XML",
        "xml-with-image": "XML + zdjęcia",
      };
      return map[value] || value;
    },
    mapBooleanValue(value) {
      if (value === true || value === "true") {
        return "Tak";
      }
      if (value === false || value === "false") {
        return "Nie";
      }
      return value;
    },
    formatMaybeDateByKey(key, value) {
      if (value == null || value === "") {
        return "-";
      }

      if (key === "dateTimeStart" || key === "dateTimeEnd") {
        return this.formatDate(value) || this.formatDetailValue(value);
      }
      if (key === "source") {
        return this.mapSourceValue(String(value));
      }
      if (key === "reportType") {
        return this.mapReportTypeValue(String(value));
      }
      if (key === "orderDirection" || key === "orderDirection1") {
        const normalized = String(value).toLowerCase();
        if (normalized === "asc") {
          return "Rosnąco";
        }
        if (normalized === "desc") {
          return "Malejąco";
        }
      }
      if (key === "eventTypes" && Array.isArray(value)) {
        return value
          .map((id) => USER_ACTIVITY_EVENT_TYPES[Number(id)] || String(id))
          .join(", ");
      }
      if (typeof value === "boolean" || value === "true" || value === "false") {
        return this.formatDetailValue(this.mapBooleanValue(value));
      }

      return this.formatDetailValue(value);
    },
    pushFilterDetail(details, label, key, value) {
      details.push({
        label: `Filtr: ${label || this.mapFilterLabel(key)}`,
        value: this.formatMaybeDateByKey(key, value),
      });
    },
    getDetailsLocation(evt, metadata) {
      return evt.location?.shortName || metadata?.locationId || "-";
    },
    createBaseDetails(evt, metadata, withSource = false, withLocation = true) {
      const details = [{ label: "Typ zdarzenia", value: this.getEventLabel(evt) }];

      if (withLocation) {
        details.push({ label: "Lokalizacja", value: this.getDetailsLocation(evt, metadata) });
      }

      details.push({ label: "Adres IP", value: evt.ip || "-" });
      details.push({ label: "Data zdarzenia", value: this.formatDate(this.getStartAt(evt)) || "-" });

      if (withSource) {
        details.splice(3, 0, {
          label: "Źródło operacji",
          value: this.formatMaybeDateByKey("source", metadata?.source ?? "-"),
        });
      }

      return details;
    },
    getEventDetails(evt) {
      const metadata = this.parseMetadata(evt.metadata) || {};
      const eventKey = this.getEventKey(evt);

      if (eventKey === "archive-search-save") {
        const details = this.createBaseDetails(evt, metadata, true);
        const usedFilters = metadata?.usedFilters && typeof metadata.usedFilters === "object" ? metadata.usedFilters : {};

        this.pushFilterDetail(
          details,
          "Użytkownik wykonujący",
          "performedByUsername",
          metadata?.performedByUsername || evt.user?.username || "-"
        );
        this.pushFilterDetail(details, null, "dateTimeStart", usedFilters.dateTimeStart ?? metadata?.dateTimeStart ?? "-");
        this.pushFilterDetail(details, null, "dateTimeEnd", usedFilters.dateTimeEnd ?? metadata?.dateTimeEnd ?? "-");
        this.pushFilterDetail(details, null, "orderDirection", usedFilters.orderDirection ?? usedFilters.orderDirection1 ?? "-");
        this.pushFilterDetail(details, null, "orderByColumnName1", usedFilters.orderByColumnName1 ?? "-");
        this.pushFilterDetail(details, null, "withImages", usedFilters.withImages ?? "-");
        this.pushFilterDetail(details, null, "withAxles", usedFilters.withAxles ?? "-");

        return details;
      }

      if (eventKey === "report-download") {
        const details = this.createBaseDetails(evt, metadata, true);

        this.pushFilterDetail(
          details,
          "Użytkownik wykonujący",
          "performedByUsername",
          metadata?.performedByUsername || evt.user?.username || "-"
        );
        this.pushFilterDetail(details, null, "reportType", metadata?.reportType ?? "-");
        this.pushFilterDetail(details, null, "dateTimeStart", metadata?.dateTimeStart ?? "-");
        this.pushFilterDetail(details, null, "dateTimeEnd", metadata?.dateTimeEnd ?? "-");

        return details;
      }

      if (eventKey === "user-add") {
        const details = this.createBaseDetails(evt, metadata, false, false);
        details.push({
          label: "Nazwa utworzonego użytkownika",
          value: this.formatDetailValue(metadata?.createdUsername),
        });
        details.push({
          label: "Nadane role",
          value: this.formatDetailValue(metadata?.assignedRoles),
        });
        return details;
      }

      if (eventKey === "user-delete") {
        const details = this.createBaseDetails(evt, metadata, false, false);
        details.push({
          label: "Nazwa usuniętego użytkownika",
          value: this.formatDetailValue(metadata?.deletedUsername),
        });
        return details;
      }

      if (eventKey === "permission-grant") {
        const details = this.createBaseDetails(evt, metadata, false, false);
        details.push({
          label: "Użytkownik wykonujący (nazwa)",
          value: this.formatDetailValue(metadata?.performedByUsername || evt.user?.username),
        });
        details.push({
          label: "Użytkownik otrzymujący (nazwa)",
          value: this.formatDetailValue(metadata?.targetUsername),
        });
        details.push({
          label: "Nadane role",
          value: this.formatDetailValue(metadata?.grantedRoles),
        });
        return details;
      }

      if (eventKey === "permission-revoke") {
        const details = this.createBaseDetails(evt, metadata, false, false);
        details.push({
          label: "Użytkownik wykonujący (nazwa)",
          value: this.formatDetailValue(metadata?.performedByUsername || evt.user?.username),
        });
        details.push({
          label: "Użytkownik otrzymujący (nazwa)",
          value: this.formatDetailValue(metadata?.targetUsername),
        });
        details.push({
          label: "Usunięta rola",
          value: this.formatDetailValue(metadata?.revokedRoles),
        });
        details.push({
          label: "Nadana nowa rola",
          value: this.formatDetailValue(metadata?.grantedRoles),
        });
        return details;
      }

      return [
        { label: "Typ zdarzenia", value: this.getEventLabel(evt) },
        { label: "Lokalizacja", value: this.getLocationLabel(evt) },
        { label: "Adres IP", value: evt.ip || "-" },
        { label: "Data zdarzenia", value: this.formatDate(this.getStartAt(evt)) || "-" },
      ];
    },
    // Oblicz czas trwania sesji na podstawie dat
    getDiff(dateFrom, dateTo) {
      if (!dateFrom || !dateTo) {
        return "";
      }
      const secDiff = moment(dateTo).diff(moment(dateFrom), "seconds");
      const hour = Math.floor(secDiff / 360);
      const min = Math.floor((secDiff - hour * 360) / 60);
      const sec = secDiff - hour * 360 - min * 60;

      return (
        (hour < 10 ? "0" + hour : hour) +
        ":" +
        (min < 10 ? "0" + min : min) +
        ":" +
        (sec < 10 ? "0" + sec : sec)
      );
    },
    getSelectedEventTypeId() {
      const raw = this.filtersStore?.filters?.eventTypes;
      if (raw == null || raw === "") {
        return null;
      }
      const id = Number(raw);
      return Number.isNaN(id) ? null : id;
    },
    getSelectedEventKey() {
      const id = this.getSelectedEventTypeId();
      return id ? USER_ACTIVITY_EVENT_KEY_BY_ID[id] ?? null : null;
    },
    getEventKey(evt) {
      if (evt?.__eventKey) {
        return evt.__eventKey;
      }
      if (evt?.vms) {
        return "vms-session";
      }

      const rawEventType = evt?.eventType ?? evt?.type ?? evt?.action ?? evt?.activityType;
      const normalized = String(rawEventType ?? "").trim().toLowerCase();

      if (normalized) {
        const normalizedMap = {
          "1": "login-www",
          "2": "login-preselection",
          "3": "vms-session",
          "4": "archive-search-save",
          "5": "report-download",
          "6": "user-add",
          "7": "user-delete",
          "8": "permission-grant",
          "9": "permission-revoke",
          "archive-search-save": "archive-search-save",
          "report-download": "report-download",
          "user-add": "user-add",
          "user-delete": "user-delete",
          "permission-grant": "permission-grant",
          "permission-revoke": "permission-revoke",
          "add-user": "user-add",
          "delete-user": "user-delete",
          "grant-permission": "permission-grant",
          "revoke-permission": "permission-revoke",
        };
        if (normalizedMap[normalized]) {
          return normalizedMap[normalized];
        }
      }

      return evt?.location ? "login-preselection" : "login-www";
    },
    getEventLabel(evt) {
      const eventKey = this.getEventKey(evt);
      const byKey = {
        "login-www": USER_ACTIVITY_EVENT_TYPES[1],
        "login-preselection": USER_ACTIVITY_EVENT_TYPES[2],
        "vms-session": USER_ACTIVITY_EVENT_TYPES[3],
        "archive-search-save": USER_ACTIVITY_EVENT_TYPES[4],
        "report-download": USER_ACTIVITY_EVENT_TYPES[5],
        "user-add": USER_ACTIVITY_EVENT_TYPES[6],
        "user-delete": USER_ACTIVITY_EVENT_TYPES[7],
        "permission-grant": USER_ACTIVITY_EVENT_TYPES[8],
        "permission-revoke": USER_ACTIVITY_EVENT_TYPES[9],
      };
      return byKey[eventKey] ?? "-";
    },
    getLocationLabel(evt) {
      const eventKey = this.getEventKey(evt);
      if (eventKey === "login-www") {
        return "SERWER WWW";
      }
      if (eventKey === "login-preselection") {
        return evt.location?.shortName ?? "-";
      }
      return evt.location?.shortName ?? "-";
    },
    getStartAt(evt) {
      return evt.loginAt ?? evt.createdAt ?? null;
    },
    getEndAt(evt) {
      const eventKey = this.getEventKey(evt);
      if (eventKey === "login-www" || eventKey === "login-preselection" || eventKey === "vms-session") {
        return evt.logoutAt ?? null;
      }
      return null;
    },
    getDurationForEvent(evt) {
      return this.getDiff(this.getStartAt(evt), this.getEndAt(evt));
    },
    formatDate(value) {
      return value ? moment(value).format("DD.MM.YYYY HH:mm:ss") : "";
    },
    // Przyciśnięcie "Szukaj" - resetuje do pierwszej strony i pobiera dane
    checkAndRefresh() {
      const nowIso = new Date().toISOString();
      this.filtersStore.addItem("dateTimeEnd", nowIso);
      if (!this.filtersStore?.filters?.dateTimeStart) {
        const dayAgoIso = new Date(new Date().getTime() - 24 * 60 * 60 * 1000).toISOString();
        this.filtersStore.addItem("dateTimeStart", dayAgoIso);
      }
      this.setPage(1);
    },
  },
};
</script>

<style scoped>
.content {
  width: 100%;
  min-height: 270px;
  margin-bottom: 20px;
}

h3 {
  padding: 3px 15px;
  background-color: white;
}
button {
  position: relative;
  padding: 5px 15px 5px 25px;
  height: 40px;
  width: 100%;
  background: white;
}
button::before {
  position: absolute;
  left: 5px;
  top: 5px;
  content: "";
  width: 30px;
  height: 30px;
  background: url("../assets/icons/szukaj.png") no-repeat left top;
}
.filters {
  border-top: 1px solid #bbbbbb;
  border-bottom: 1px solid #bbbbbb;
}
.user-table {
  width: 100%;
  font-size: 12px;
  color: black;
}
.activity-row {
  cursor: pointer;
}
.activity-row.activity-row-open {
  background-color: #eef3ff;
}
.activity-details-row {
  background-color: #f8f9fc;
}
.activity-details-cell {
  padding: 10px 14px;
}
.activity-details-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 6px 14px;
}
.activity-detail-item {
  font-size: 12px;
  line-height: 1.4;
}
.activity-detail-label {
  font-weight: 600;
  margin-right: 4px;
}
.activity-detail-value {
  color: #333;
}

.pager {
  margin-bottom: 1rem;
  padding-bottom: 1rem;
}
</style>
